import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LockIcon } from 'lucide-react';
import { AdminBarChart, DistributionList } from '../../components/admin/AdminChart';
import { AdminMetricCard, AdminPageTitle, AdminTabs, ConfirmationModal, Panel, StatusBadge } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { adminBadges, levelDistribution, progressionRules, recordAudit, streakBuckets, xpDistribution, type AdminBadge } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
import { ConfigParamsPanel } from '../../components/admin/ConfigParamsPanel';
import { FixedRulesPanel } from '../../components/admin/FixedRulesPanel';
import { ChallengeAdmin } from '../../components/admin/ChallengeAdmin';
import { engagementAdminSections, type EngagementAdminSection } from '../../data/engagementAdmin';
import { hasCapability, prototypeAdminRole } from '../../data/adminOperations';
const tabs = [{
  id: 'xp',
  label: 'Bee'
}, {
  id: 'niveis',
  label: 'Níveis'
}, {
  id: 'sequencias',
  label: 'Sequências'
}, {
  id: 'medalhas',
  label: 'Medalhas'
}, {
  id: 'desafios',
  label: 'Desafios'
}, {
  id: 'protecao',
  label: 'Proteção de Sequência'
}, {
  id: 'colmeia',
  label: 'Minha Colmeia'
}, {
  id: 'bau',
  label: 'Baú da Colmeia'
}, {
  id: 'liga',
  label: 'Liga'
}, {
  id: 'retrospectiva',
  label: 'Retrospectiva'
}];
const engagementTabs: Record<string, EngagementAdminSection> = {
  protecao: 'protection',
  colmeia: 'hive',
  bau: 'chest',
  liga: 'league',
  retrospectiva: 'recap'
};
export function AdminGamification() {
  const {
    tab: param
  } = useParams();
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const tab = tabs.some(item => item.id === param) ? param as string : 'xp';
  const [editing, setEditing] = useState<AdminBadge | null>(null);
  const badgeColumns: Column<AdminBadge>[] = [{
    id: 'name',
    header: 'Medalha',
    sortValue: row => row.name,
    cell: row => <span className="text-[13px] font-medium text-deep" data-id="element-1664" data-mp="1z7r3qmp1vs59">{row.name}</span>
  }, {
    id: 'rule',
    header: 'Regra de qualificação',
    width: '34%',
    cell: row => <span className="text-[12.5px] text-muted" data-id="element-1665" data-mp="1pgec9w75z3zr">{tl(row.rule)}</span>
  }, {
    id: 'unlocked',
    header: 'Desbloqueios',
    align: 'right',
    sortValue: row => Number(row.unlocked.replace(/\./g, '')),
    cell: row => <span className="bee-num font-bold text-deep" data-id="element-1666" data-mp="1giu8afhx2tlg">{row.unlocked}</span>
  }, {
    id: 'status',
    header: 'Status',
    cell: row => <StatusBadge status={row.status} label={row.status === 'active' ? 'Ativa' : 'Rascunho'} data-id="element-1667" data-mp="16jmw3vmx54zx" />
  }, {
    id: 'created',
    header: 'Criada em',
    sortValue: row => row.created,
    cell: row => <span className="bee-num text-muted" data-id="element-1668" data-mp="30x47yldyodf4">{row.created}</span>
  }];
  return <div className="space-y-4" data-id="element-1669" data-mp="39cttduu1irdd">
      <AdminPageTitle title="Gamificação" subtitle="Distribuição de Bee, níveis, sequências, medalhas e recursos de engajamento. Regras fixas do produto são somente leitura; parâmetros configuráveis exigem permissão." data-id="element-1670" data-mp="39biofrouzdko" />

      <AdminTabs tabs={tabs} active={tab} onChange={next => navigate(`/admin/gamificacao/${next}`)} data-id="element-1671" data-mp="3n5jjaacowgfr" />

      {tab === 'xp' && <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1672" data-mp="2qgk56epfckfn">
            <AdminMetricCard label="Bee médio por usuário" value="74,2" delta={0.9} accent data-id="element-1673" data-mp="2uhdtvaaoy39l" />
            <AdminMetricCard label="Usuários em 100 Bee hoje" value="31.208" delta={6.7} data-id="element-1674" data-mp="2zee13oja1c2a" />
            <AdminMetricCard label="Acima do gate de 50 Bee" value="81.040" delta={4.2} data-id="element-1675" data-mp="34esab9wm6s1w" />
            <AdminMetricCard label="Bônus de foto usados" value="12.744" delta={2.8} data-id="element-1676" data-mp="eppyvkf8mktf" />
          </section>

          <AdminBarChart title="Distribuição de Bee no dia" question="Quantos usuários param antes do gate de 50 Bee?" data={xpDistribution} xKey="range" series={[{
        id: 'users',
        name: 'Usuários'
      }]} data-id="element-1677" data-mp="jni2vct5pxdz" />

          <Panel title="Regras de progressão" hint="Configuração vigente, em modo somente leitura." data-id="element-1678" data-mp="21lwkw9irvku5">
            <ul className="space-y-2" data-id="element-1679" data-mp="1wmtdwjj8f4jj">
              {progressionRules.map(rule => <li key={rule.id} className="flex items-center justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-1680" data-mp="1wp61bc7re73z">
                  <span className="text-[12.5px] text-deep" data-id="element-1681" data-mp="26j6em1d72ins">{tl(rule.label)}</span>
                  <span className="flex items-center gap-2" data-id="element-1682" data-mp="2keiaic336f6a">
                    <span className="bee-num text-[12.5px] font-bold text-deep" data-id="element-1683" data-mp="2gbvfy3f16g5e">
                      {rule.value}
                    </span>
                    <LockIcon size={13} className="text-muted" aria-hidden="true" data-id="element-1684" data-mp="2u8h6sughb4qb" />
                  </span>
                </li>)}
            </ul>
            <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-1685" data-mp="38ybum9bjcfrd">
              A base de 100 Bee, o gate de 50 Bee e o bônus de +10 Bee fora da base são regras
              críticas do produto. A edição exige aprovação de Super Admin e não é exposta
              aqui.
            </p>
          </Panel>
        </>}

      {tab === 'niveis' && <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-1686" data-mp="2q6sss5t8z7qx">
          <AdminBarChart title="Usuários por nível" question="A base está avançando de nível ou concentrada no início?" data={levelDistribution} xKey="level" series={[{
        id: 'users',
        name: 'Usuários'
      }]} data-id="element-1687" data-mp="2q5htai6u4m5m" />
          <Panel title="Proporção por nível" data-id="element-1688" data-mp="2l8hm23y91dcx">
            <DistributionList items={levelDistribution.map(level => ({
          label: level.level,
          value: level.users,
          secondary: level.users.toLocaleString('pt-BR')
        }))} data-id="element-1689" data-mp="2l5dd6t9u9tfx" />
          </Panel>
        </div>}

      {tab === 'sequencias' && <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1690" data-mp="cz1nfxaq6x1g">
            <AdminMetricCard label="Sequências ativas" value="31.208" delta={6.7} accent data-id="element-1691" data-mp="80qctkclwii6" />
            <AdminMetricCard label="Média da sequência ativa" value="5,4 dias" delta={1.2} data-id="element-1692" data-mp="31x1nyksl3l6" />
            <AdminMetricCard label="Quebras ontem" value="2.917" delta={-3.1} data-id="element-1693" data-mp="10pnk6sfa4uw5" />
            <AdminMetricCard label="Acima de 30 dias" value="228" delta={9.4} data-id="element-1694" data-mp="10nkw4dxotbu2" />
          </section>
          <AdminBarChart title="Sequências por faixa" question="Onde as sequências costumam quebrar?" data={streakBuckets} xKey="bucket" series={[{
        id: 'users',
        name: 'Usuários'
      }]} horizontal data-id="element-1695" data-mp="vohqar9vv1l1" />
        </>}

      {tab === 'medalhas' && <>
          <DataTable rows={adminBadges} columns={badgeColumns} rowKey={row => row.id} pageSize={10} actions={[{
        id: 'edit',
        label: 'Editar regra',
        onSelect: setEditing
      }]} data-id="element-1696" data-mp="37flq3j4bpqhp" />
          <Panel title="Edição de medalhas" data-id="element-1697" data-mp="1qxbnbgphmlpp">
            <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1698" data-mp="1vxprgnnyxbig">
              Alterar a regra de uma medalha muda a qualificação de toda a base. Por isso a
              edição exige confirmação explícita e fica registrada na auditoria com a regra
              anterior e a nova.
            </p>
          </Panel>
        </>}

      {tab === 'desafios' && <ChallengeAdmin data-id="element-1699" data-mp="2ep8tosa64ljf" />}

      {engagementTabs[tab] && <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_1.4fr]" data-id="element-1700" data-mp="2eps5dr986dp0">
          <FixedRulesPanel rules={engagementAdminSections[engagementTabs[tab]].fixedRules} data-id="element-1701" data-mp="2jnafhkyzayyl" />
          <ConfigParamsPanel key={tab} section={tl(engagementAdminSections[engagementTabs[tab]].title)} params={engagementAdminSections[engagementTabs[tab]].params} canEdit={hasCapability(prototypeAdminRole, 'engagement.manageConfig')} data-id="element-1702" data-mp="kc0t0jzihfpv" />
        </div>}

      <ConfirmationModal open={editing !== null} title={`Editar a regra de ${editing?.name ?? ''}?`} body="A nova regra revalida a qualificação de toda a base, o que pode conceder ou remover a medalha de usuários já atendidos. A alteração fica registrada na auditoria." confirmLabel="Entendi, continuar" onConfirm={() => {
      if (editing) {
        recordAudit({
          action: {
            'pt-BR': 'Editou regra de medalha',
            'en-US': 'Edited badge rule'
          },
          entity: `badge:${editing.id}`,
          context: {
            'pt-BR': editing.name,
            'en-US': editing.name
          },
          before: tl(editing.rule),
          severity: 'sensitive'
        });
      }
      setEditing(null);
    }} onClose={() => setEditing(null)} data-id="element-1703" data-mp="2n5hmwpm6o7hz" />
    </div>;
}